<template>
    <Channels v-if="!isDesktop"/>
    <div class="desktop-live page" v-else>
        <div class="live-header">
            <div class="filter-row">
                <div class="country-filters">
                    <div v-for="c in countries" :key="c.code"
                        class="country-pill"
                        :class="{ active: activeCountry === c.code }"
                        v-ripple
                        @click="changeCountry(c.code)"
                    >
                        <img :src="c.flag" />
                        <span>{{ c.label }}</span>
                    </div>
                </div>

                <div class="divider"></div>

                <div class="sport-filters">
                    <div class="filter-pill"
                        v-for="(s, i) in sportsData" :key="i"
                        :class="{ active: activeSportFilter === s.name }"
                        v-ripple
                        @click="changeSportFilter(s.name)"
                    >
                        <img :src="s.img" />
                        <span>{{ s.name }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="activeLive.length+notLiveSports.length > 0" class="live-section">
            <h2>{{activeLive.length > 0 ? 'Live' : ''}} Sports</h2>
            <div class="scroll-row sport">
                <div class="sport-hover-wrap" v-for="(s, i) in activeLive" :key="'live-' + i" >
                    <Sport :sport="s" v-ripple mode="live"/>
                </div>
            </div>

            <div class="scroll-row sport">
                <Sport v-for="(s, i) in notLiveSports" :key="'upcoming-' + i" :sport="s" mode="list" v-ripple/>
            </div>
        </div>

        <div v-if="epgs.length > 0" class="live-section">
            <div class="section-header">
                <h2>Program Guide</h2>
                <router-link to="/epg" class="see-all" v-ripple>see all</router-link>
            </div>
            <div class="scroll-row epg">
                <ContentGuide v-for="(e, i) in epgs" :key="'cg-' + i" :guide="e" />
            </div>

        <div v-if="epgs.length > 0" class="live-section">
            <div class="result-items content-row epg">
                <template v-for="(e, i) in epgs.slice(0,2)">
                    <div class="content-row-epg-wrapper"  :key="'epg-' + i">
                        <ListGuide :guide="e"   v-ripple />
                    </div>

                </template>
            </div>
        </div>
        </div>

        <div v-if="saved.length > 0" class="live-section">
            <h2>Saved Channels</h2>
            <div class="scroll-row">
                <SavedChannel v-for="(c, i) in saved" :key="i" :channel="c" />
            </div>
        </div>

        <div v-for="(g, j) in lazyChannelGroups[activeCountry]" :key="j + activeCountry + g.id" class="live-section">
            <h2>{{ g[0].group }}</h2>
            <div class="scroll-row">
                <div v-for="(c, i) in g" :key="i + activeCountry + c.id" class="channel-hover-wrap" >
                    <Channel :channel="c" />
                </div>
            </div>

            <div class="result-items content-row epg">

                <template v-for="(e, i) in epgs.slice((2+(j*2)), (2+(j*2+2)) )">
                    <div class="content-row-epg-wrapper"  :key="'epg-' + i">
                        <ListGuide :guide="e"  v-ripple />
                    </div>

                </template>
            </div>

        </div>

        <div v-if="ready && stfs.length > 0" class="live-section">
            <h2>Live Stations</h2>
            <div class="scroll-row">
                <div v-for="(c, i) in stfs.slice(0, 40)" :key="i + 'tfs'" class="channel-hover-wrap">
                    <Channel :channel="c" />
                </div>
            </div>
        </div>


        <Loader v-if="!ready" />
    </div>
</template>

<script>
const Channels = () => import("@/views/Channels.vue");
const Channel = () => import('@/components/Channel')
const Sport = () => import('@/components/Sport')
const SavedChannel = () => import('@/components/SavedChannel')
const ContentGuide = () => import('@/components/ContentGuide')
const ListGuide = () => import('@/components/ListGuide')
const Loader = () => import('@/components/Loader')

export default {
    name: 'DesktopLive',

    components: {
        Channels,
        Channel,
        Sport,
        SavedChannel,
        ContentGuide,
        ListGuide,
        Loader,
    },

    data() {
        return {
            channels: { USA: [], CA: [], UK: [], TFS: [] },
            allChannelGroups: { USA: [], CA: [], UK: [], TFS: [] },
            lazyChannelGroups: { USA: [], CA: [], UK: [], TFS: [] },
            saved: [],
            tfs: [],
            live: [],
            allSports: [],
            activeLive: [],
            activeSports: [],
            sportsData: [],
            epgs: [],
            activeCountry: 'USA',
            activeSportFilter: '',
            ready: false,
            countries: [
                { code: 'USA', label: 'US', flag: 'https://flagicons.lipis.dev/flags/4x3/um.svg' },
                { code: 'CA', label: 'CA', flag: 'https://flagicons.lipis.dev/flags/4x3/ca.svg' },
                { code: 'UK', label: 'UK', flag: 'https://flagicons.lipis.dev/flags/4x3/gb.svg' },
            ],
        }
    },

    computed: {
        stfs() {
            return window.shuffle(this.tfs)
        },
        notLiveSports() {
            const liveIds = new Set(this.activeLive.map(s => s.id))
            return this.activeSports.filter(s => !liveIds.has(s.id))
        },
    },

    mounted() {
        document.title = 'Live Sports and Network TV - QB'
        this.sportsData = window.sportsData || []
        this.loadChannels()
        this.loadTFS()
        this.loadSports()
        this.loadSaved()
        this.loadGuides()
    },

    activated() {
        document.title = 'Live Sports and Network TV - QB'
    },

    methods: {
        async loadChannels() {
            try {
                const res = await window.afetch('/channels?country=' + this.activeCountry)
                const data = await res.json()
                this.channels[this.activeCountry] = data[this.activeCountry]
                this.setChannelGroups()
            } catch (e) {
                this.ready = true
            }
        },

        async loadTFS() {
            try {
                const res = await window.afetch('/channels?country=24/7')
                const data = await res.json()
                this.tfs = data['TFS']
            } catch (e) {
                // tfs stays empty
            }
        },

        async loadSports() {
            try {
                const liveRes = await window.afetch('/liveSports')
                const liveData = await liveRes.json()
                this.live = liveData.live
                this.activeLive = liveData.live

                const allRes = await window.afetch('/allSports')
                const allData = await allRes.json()
                this.allSports = allData.live
                this.activeSports = allData.live
            } catch (e) {
                // sports stays empty
            }
        },

        async loadSaved() {
            try {
                const res = await window.afetch('/history')
                const data = await res.json()
                this.saved = data.saves
            } catch (e) {
                // saved stays empty
            }
        },

        async loadGuides() {
            try {
                const res = await window.afetch('/guides')
                const data = await res.json()
                this.epgs = data.guides
            } catch (e) {
                // epgs stays empty
            }
        },

        changeCountry(country) {
            if (country === this.activeCountry) return
            this.ready = false
            this.activeCountry = country
            this.$nextTick(() => {
                if (this.channels[this.activeCountry].length === 0) {
                    this.loadChannels()
                } else {
                    this.ready = true
                }
            })
        },

        changeSportFilter(s) {
            this.activeLive = []
            this.activeSports = []
            this.$nextTick(() => {
                if (s === this.activeSportFilter) {
                    this.activeSportFilter = ''
                    this.activeLive = this.live
                    this.activeSports = this.allSports
                } else {
                    this.activeSportFilter = s
                    this.activeLive = this.live.filter(x => x.type === s)
                    this.activeSports = this.allSports.filter(x => x.type === s)
                }
            })
        },

        groupBy(arr, key) {
            return arr.reduce((result, obj) => {
                const groupKey = obj[key]
                if (!result[groupKey]) {
                    result[groupKey] = []
                }
                result[groupKey].push(obj)
                return result
            }, {})
        },

        setChannelGroups() {
            this.allChannelGroups[this.activeCountry] = Object.values(
                this.groupBy(this.channels[this.activeCountry], 'group')
            ).sort((a, b) => {
                if (a[0].group < b[0].group) return -1
                if (a[0].group > b[0].group) return 1
                return 0
            })
            this.lazyChannelGroups[this.activeCountry] = this.allChannelGroups[this.activeCountry]
            this.ready = true
        },



        navigateTo(path) {
            this.$router.push(path).catch(() => {})
        },

    },
}
</script>

<style lang="scss" scoped>
.desktop-live {
    padding: 24px 0;
    margin-top: 56px;
}

.live-header {
    margin-bottom: 32px;
}

h2{
    padding-left: 48px;
}

.filter-row {
    padding: 0 48px;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.divider {
    width: 1px;
    height: 28px;
    background-color: rgba(255, 255, 255, 0.15);
}

.country-filters,
.sport-filters {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.country-pill,
.filter-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.08);
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(255, 255, 255, 0.15);
    }

    &.active {
        background-color: white;
        color: black;

        span {
            color: black;
            font-weight: 600;
        }
    }

    img {
        width: 20px;
        height: 15px;
        border-radius: 2px;
    }

    span {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.7);
    }
}

.filter-pill img {
    width: 24px;
    height: 24px;
    border-radius: 4px;
}

.live-section {
    margin-bottom: 32px;

    h2 {
        margin: 0 0 16px 0;
        font-size: 20px;
        font-weight: 600;
    }

    h3 {
        margin: 0 0 16px 0;
        font-size: 16px;
        font-weight: 600;
        padding-left: 4px;
    }
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 48px);
    margin-bottom: 16px;

    h2 {
        margin: 0;
    }

    .see-all {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        text-decoration: none;

        &:hover {
            color: white;
        }
    }
}

.content-row.epg{
    flex-direction: column;
    width: 100%;
    overflow: visible;
    padding: 0;

    .content-row-epg-wrapper{
        padding-left: 48px;
        margin: 5px 0;
        display: flex;
        flex-grow: 1;
        overflow-x: scroll;
    }
}


.scroll-row {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 8px;
    padding-left: 48px;
}

</style>
